import React, {
  useState, useMemo, useEffect, useCallback, ReactElement,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FieldSelect from '../../../../components/field-select/field-select.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import ChevronSlider from '../../../../components/chevron-slider/chevron-slider.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import FormatService from '../../../../services/format-service';
import useScreenSmall from '../../../../hooks/use-screen-small';
import setStatusFilter from './status-filter.action';
import { FILTER_VALUE_EMPTY } from '../../../../constants/system/filter-values';

const StatusFilter: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isDisabled,
    statusFilterList,
    statusFilterStatus,
    onStatusFilterValueChange,
    onBeforeStatusFilterValueChange,
  } = props;

  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isNavigationDrawerOpen: boolean = useSelector(({ navigation }: Reducers): boolean => (
    navigation.isDrawerOpen
  ));
  const isRightDrawerOpen: boolean = useSelector(({ rightDrawer }: Reducers): boolean => (
    rightDrawer.isDrawerOpen
  ));

  const [filterStatus, setFilterStatus]: [string | null, Function] = useState(null);

  const controlsDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isNavigationDrawerOpen, isRightDrawerOpen,
  ], [isNavigationDrawerOpen, isRightDrawerOpen]);

  const chevronItemList: ChevronItem[] = statusFilterList && statusFilterList.length
    ? statusFilterList.map((statusFilterItem: StatusFilterItem): ChevronItem => ({
      id: statusFilterItem.id,
      value: statusFilterItem.name,
      title: statusFilterItem.title,
      subTitle: (statusFilterItem.itemsTotal || 0).toString(),
    })) : [];

  const menuItemStatusFilterList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'title',
    optionItemDetailsLabelKey: 'itemsTotal',
    optionList: statusFilterList,
    selectedValue: filterStatus,
  }), [statusFilterList, filterStatus]);

  const statusFilterValueChange: Function = useCallback((status: string): void => {
    const selectedStatus: string | null = FormatService.parseInt(status) !== FILTER_VALUE_EMPTY ? status : null;
    const isCurrentFilterSet: boolean = !!filterStatus && selectedStatus === filterStatus;
    const currentStatus: string | null = !isCurrentFilterSet ? selectedStatus : null;

    if (onBeforeStatusFilterValueChange) {
      onBeforeStatusFilterValueChange(currentStatus);
    }

    if (isDisabled) {
      return;
    }

    setFilterStatus(currentStatus);

    dispatch(setStatusFilter(currentStatus));

    if (onStatusFilterValueChange) {
      onStatusFilterValueChange(currentStatus);
    }
  }, [filterStatus, isDisabled]);

  useEffect((): void => {
    if (!filterStatus || filterStatus !== statusFilterStatus) {
      setFilterStatus(statusFilterStatus || null);
    }
  }, [statusFilterStatus]);

  return (
    <PaddedFlexContainer isDefault={!isSmallScreen}>
      {
        !isSmallScreen && (
          <ChevronSlider
            activeValue={filterStatus}
            chevronItemList={chevronItemList}
            controlsDeps={controlsDeps}
            onChange={statusFilterValueChange}
          />
        )
      }
      {
        isSmallScreen && (
          <FieldSelect
            isPlain
            isBordered
            withBlankElement
            withBlankItemDefault
            isDisabled={statusFilterList && !statusFilterList.length}
            value={filterStatus}
            blankElementValue={FILTER_VALUE_EMPTY}
            blankElementLabel="All"
            onChange={(event: EventSelectChange, value: string): void => {
              statusFilterValueChange(value);
            }}
          >
            {menuItemStatusFilterList}
          </FieldSelect>
        )
      }
    </PaddedFlexContainer>
  );
});

export default StatusFilter;
